import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import axios from 'axios';
import App from './App';

import { getToken } from './auth';

// Global axios config — send HttpOnly JWT cookie with every request
axios.defaults.withCredentials = true;

// Patch global fetch — add Authorization header from in-memory token as fallback.
// The HttpOnly cookie is still sent via credentials:'include', but cross-origin
// scenarios (dev) may block the cookie. The header always works.
const _origFetch = window.fetch;
window.fetch = (url, options = {}) => {
  const token = getToken();
  if (token) {
    options.headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }
  return _origFetch(url, options);
};

// Request interceptor — attach in-memory token as Authorization header fallback.
// If the HttpOnly cookie works (same-origin), the backend reads the cookie first.
// If cross-origin (dev), the cookie may be blocked but the header gets through.
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global axios error interceptor — catches ALL axios errors across the app.
// Returns a fallback response instead of rejecting, so callers without try/catch
// get { data: null } instead of an unhandled promise rejection.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || 'unknown';
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.warn(`[API] Network error — ${url}`);
    } else if (error.response?.status === 401) {
      console.warn(`[Auth] 401 session expired — ${url}`);
      // Force-logout. Triggered when:
      //   - Token expired naturally
      //   - SUPERADMIN flipped enabled=false on this user (or their tenant
      //     parent). JwtRequestFilter rejects the cached/DB-checked user
      //     via DisabledException, request proceeds unauthenticated, server
      //     returns 401. Clear local state and bounce to /login so the user
      //     sees the disabled-account error on the next login attempt.
      // Guarded by uid + path so the login page itself doesn't loop.
      try {
        if (localStorage.getItem('uid')
            && !window.location.pathname.startsWith('/login')
            && !String(url).includes('/login')) {
          localStorage.clear();
          window.location.href = '/login';
        }
      } catch (e) { /* localStorage unavailable; nothing we can do */ }
    } else if (error.response?.status === 403) {
      console.warn(`[Auth] 403 forbidden — ${url}`);
    } else {
      console.warn(`[API] ${error.response?.status || 'ERR'} — ${url}`);
    }
    // Return a safe fallback so callers without try/catch don't crash
    return { data: null, status: error.response?.status || 0, _error: true };
  }
);

// Global unhandled promise rejection handler — catches any async error
// that isn't wrapped in try/catch (e.g. bare API calls in useEffect).
// Logs a meaningful message instead of crashing React with flushPassiveEffects.
window.addEventListener('unhandledrejection', (event) => {
  const err = event.reason;
  const msg = err?.message || String(err);

  // Network errors (backend down, CORS, timeout) — expected during dev
  if (msg.includes('Network Error') || msg.includes('Failed to fetch') || msg.includes('Load failed')) {
    console.warn('[API] Network error — backend may be unreachable:', msg);
    event.preventDefault(); // Suppress the red error in console
    return;
  }

  // 401/403 — session expired or forbidden
  if (err?.response?.status === 401 || err?.response?.status === 403) {
    console.warn('[Auth] Session expired or forbidden:', msg);
    event.preventDefault();
    return;
  }

  // Everything else — log with context but don't crash the page
  console.warn('[Unhandled]', msg, err);
  event.preventDefault();
});

// Global error handler for synchronous errors
window.addEventListener('error', (event) => {
  // Suppress ResizeObserver loop errors (benign, caused by rapid layout changes)
  if (event.message?.includes('ResizeObserver')) {
    event.preventDefault();
    return;
  }
});

ReactDOM.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById('root')
);
